import { useCallback } from 'react';
import {
  getFirestore,
  query,
  collection,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import MiniSearch from 'minisearch';
import logger from '../utils/logger';
import { getSearchIndexTtlMs } from '../utils/searchIndexTtl';

const db = getFirestore();

/**
 * Client-side song search.
 *
 * Reads at most `scanLimit` songs (newest first) once per session, then
 * queries a MiniSearch index on top of that snapshot. Subsequent searches
 * reuse the cached docs + index — only the first call pays the Firestore
 * read + indexing cost.
 *
 * Configuration:
 * - REACT_APP_SEARCH_FULL_CATALOG=true bumps the default scan from 12k → 20k.
 * - REACT_APP_SEARCH_SCAN_LIMIT=<n> overrides (hard max 25k).
 *
 * The exported `searchSongs(query, limit, offset)` signature matches the
 * previous string-match implementation so `useSearchQuery` / `useSearchPage`
 * can call it unchanged.
 */
function getSearchScanConfig() {
  const fullCatalog = process.env.REACT_APP_SEARCH_FULL_CATALOG === 'true';
  const raw = process.env.REACT_APP_SEARCH_SCAN_LIMIT;
  const parsed = raw ? parseInt(String(raw), 10) : NaN;
  const defaultLimit = fullCatalog ? 20000 : 12000;
  let scanLimit = Number.isFinite(parsed) && parsed >= 1 ? parsed : defaultLimit;
  scanLimit = Math.min(scanLimit, 25000);
  return { fullCatalog, scanLimit };
}

// Strip combining marks so a search for "jose" still finds "José" and vice
// versa. MiniSearch applies this on both the indexed tokens and the query
// terms, so the two sides always line up.
function foldDiacritics(term) {
  return term.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

function processTerm(term) {
  if (!term) return null;
  const folded = foldDiacritics(String(term).toLowerCase()).trim();
  return folded || null;
}

const MINI_SEARCH_OPTIONS = {
  idField: 'id',
  fields: ['songName', 'songArtist', 'genres', 'tags', 'description'],
  // Fields we need to render results from memory without re-reading docs.
  storeFields: [
    'id',
    'songName',
    'songArtist',
    'description',
    'genres',
    'tags',
    'thumbnailURL',
    'URL',
    'userId',
    'timestamp',
    'date',
    'likes',
    'comments',
  ],
  processTerm,
  searchOptions: {
    processTerm,
    // Boosts match the product intent: song name > artist > genre/tag >
    // description. "AND" keeps two-word queries (`radiohead karma`) tight
    // instead of falling back to a noisy OR.
    boost: { songName: 3, songArtist: 2, genres: 1.5, tags: 1.5, description: 1 },
    prefix: true,
    fuzzy: 0.2,
    combineWith: 'AND',
  },
};

// Session-scoped cache. Rebuilding the index on every search (or every
// hook call) would negate the whole point of MiniSearch — we want the
// Firestore fetch + token pipeline to run once per tab.
let songCache = null; // { docs, scanLimit, scannedCount, fullCatalog, mayHaveMoreInDb, builtAt }
let songIndex = null; // MiniSearch instance
let inflightLoad = null; // Promise<void> dedupe for concurrent callers

function resetCache() {
  songCache = null;
  songIndex = null;
  inflightLoad = null;
}

function isSongIndexStale() {
  if (!songCache || typeof songCache.builtAt !== 'number') return true;
  return Date.now() - songCache.builtAt >= getSearchIndexTtlMs();
}

async function ensureIndex() {
  if (songIndex && songCache) {
    if (!isSongIndexStale()) return;
    songCache = null;
    songIndex = null;
  }
  if (inflightLoad) return inflightLoad;

  inflightLoad = (async () => {
    const { fullCatalog, scanLimit } = getSearchScanConfig();
    logger.debug('[Search] Building song index', {
      scanLimit,
      fullCatalogMode: fullCatalog,
    });

    const snapshot = await getDocs(
      query(
        collection(db, 'songs'),
        orderBy('timestamp', 'desc'),
        limit(scanLimit)
      )
    );

    const docs = snapshot.docs.map((doc) => {
      const data = doc.data();
      // Normalize arrays so MiniSearch can index them uniformly. We also
      // flatten objects we know we never search against (timestamps) but
      // keep them stored for rendering.
      const genres = Array.isArray(data.genres) ? data.genres : [];
      const tags = Array.isArray(data.tags) ? data.tags : [];
      return {
        ...data,
        id: doc.id,
        genres,
        tags,
      };
    });

    const index = new MiniSearch(MINI_SEARCH_OPTIONS);
    index.addAll(docs);

    songCache = {
      docs,
      scanLimit,
      scannedCount: docs.length,
      fullCatalog,
      mayHaveMoreInDb: snapshot.docs.length >= scanLimit,
      builtAt: Date.now(),
    };
    songIndex = index;

    logger.debug('[Search] Song index ready', {
      docs: docs.length,
      fields: MINI_SEARCH_OPTIONS.fields,
    });
  })().catch((error) => {
    // Let the next call retry after a failure instead of caching the
    // error forever.
    inflightLoad = null;
    throw error;
  });

  try {
    await inflightLoad;
  } finally {
    inflightLoad = null;
  }
}

const useSearchSongs = () => {
  const searchSongs = useCallback(
    async (searchQuery, resultLimit = 20, offset = 0) => {
      try {
        const trimmed = (searchQuery || '').trim();
        if (!trimmed) {
          return { results: [], totalCount: 0, hasMore: false, meta: null };
        }

        await ensureIndex();
        if (!songIndex || !songCache) {
          return { results: [], totalCount: 0, hasMore: false, meta: null };
        }

        const hits = songIndex.search(trimmed);
        // MiniSearch returns match metadata (score, terms) plus every
        // stored field — merge back to the canonical doc shape the UI
        // expects (the same shape the old `.includes()` path produced).
        const byId = new Map();
        songCache.docs.forEach((d) => byId.set(d.id, d));
        const ranked = hits
          .map((hit) => byId.get(hit.id))
          .filter(Boolean);

        const totalCount = ranked.length;
        const page = ranked.slice(offset, offset + resultLimit);

        return {
          results: page,
          totalCount,
          hasMore: offset + resultLimit < totalCount,
          meta: {
            scanLimit: songCache.scanLimit,
            scannedCount: songCache.scannedCount,
            fullCatalog: songCache.fullCatalog,
            mayHaveMoreInDb: songCache.mayHaveMoreInDb,
            engine: 'minisearch',
          },
        };
      } catch (error) {
        logger.error('[Search] Error searching songs:', error);
        return { results: [], totalCount: 0, hasMore: false, meta: null };
      }
    },
    []
  );

  return { searchSongs };
};

export { resetCache as resetSongSearchCache };
export default useSearchSongs;
